import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { campaignAPI, authAPI } from '../../services/api';
import { toast } from 'react-toastify';
import FileUploadProgress from '../Common/FileUploadProgress';
import {
    PhoneIcon,
    ChatBubbleLeftRightIcon,
    SpeakerWaveIcon,
    DocumentArrowUpIcon,
    PlusIcon,
    XMarkIcon,
    ExclamationTriangleIcon,
    CheckIcon,
    CloudArrowUpIcon,
    MusicalNoteIcon
} from '@heroicons/react/24/outline';

const UserCreateCampaign = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStage, setUploadStage] = useState('uploading');
    const [uploadSpeed, setUploadSpeed] = useState(0);
    const [processedRecords, setProcessedRecords] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [validRecords, setValidRecords] = useState(0);
    const [invalidRecords, setInvalidRecords] = useState(0);
    const [duplicateRecords, setDuplicateRecords] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    // New state for database write progress
    const [recordsWritten, setRecordsWritten] = useState(0);
    const [recordsPending, setRecordsPending] = useState(0);
    const [writeProgress, setWriteProgress] = useState(0);
    const [eventSource, setEventSource] = useState(null);

    const [formData, setFormData] = useState({
        campaignName: '',
        campaignType: 'SMS',
        message: '',
        csvFile: null,
        audioFile: null,
        mediaFile: null,
        manualPhoneNumbers: '',
        phoneNumberOption: 'manual' // 'manual' or 'csv'
    });

    const [fileInfo, setFileInfo] = useState({
        csv: null,
        audio: null,
        media: null
    });

    const [errors, setErrors] = useState({});
    const [phoneNumbers, setPhoneNumbers] = useState([]);

    // Cleanup SSE connection on component unmount
    useEffect(() => {
        return () => {
            disconnectProgressStream();
        };
    }, []);

    const campaignTypeOptions = [
        { value: 'SMS', label: 'SMS', icon: PhoneIcon, description: 'Send text messages' },
        { value: 'Voice Call', label: 'Voice Call', icon: SpeakerWaveIcon, description: 'Make voice calls' },
        { value: 'Whatsapp', label: 'WhatsApp', icon: ChatBubbleLeftRightIcon, description: 'Send WhatsApp messages' }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear specific error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        if (name === 'message' && formData.campaignType === 'Whatsapp' && errors.mediaFile) {
            setErrors(prev => ({
                ...prev,
                mediaFile: ''
            }));
        }
    };

    const handleCampaignTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            campaignType: type,
            message: '',
            audioFile: null,
            mediaFile: null
        }));

        // Clear file info for audio and media when switching types
        setFileInfo(prev => ({
            ...prev,
            audio: null,
            media: null
        }));

        // Clear related errors
        setErrors(prev => ({
            ...prev,
            message: '',
            audioFile: '',
            mediaFile: ''
        }));
    };

    const handlePhoneNumberOptionChange = (option) => {
        setFormData(prev => ({
            ...prev,
            phoneNumberOption: option,
            csvFile: null,
            manualPhoneNumbers: ''
        }));

        setFileInfo(prev => ({
            ...prev,
            csv: null
        }));

        setPhoneNumbers([]);

        setErrors(prev => ({
            ...prev,
            csvFile: '',
            manualPhoneNumbers: '',
            phoneNumbers: ''
        }));
    };

    const validateManualPhoneNumbers = (numbersText) => {
        if (!numbersText.trim()) {
            return { validPhones: [], invalidPhones: [], totalNumbers: 0 };
        }

        const phoneArray = numbersText.split(',').map(phone => phone.trim()).filter(phone => phone);
        const validPhones = [];
        const invalidPhones = [];

        phoneArray.forEach((phone, index) => {
            // Basic phone validation - digits with optional + and spaces
            const cleanPhone = phone.replace(/\s/g, '');
            if (/^[\+]?[\d\-\(\)]{10,15}$/.test(cleanPhone)) {
                validPhones.push(cleanPhone);
            } else {
                invalidPhones.push({ line: index + 1, phone });
            }
        });

        return {
            validPhones: [...new Set(validPhones)], // Remove duplicates
            invalidPhones,
            totalNumbers: validPhones.length
        };
    };

    const validateCSVFile = async (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const text = e.target.result;
                    const lines = text.split('\n').filter(line => line.trim());

                    if (lines.length === 0) {
                        reject(new Error('File is empty'));
                        return;
                    }

                    if (lines.length < 2) {
                        reject(new Error('CSV file must have a header row and at least one data row'));
                        return;
                    }

                    // Check record count limit (500,000 records excluding header)
                    const recordCount = lines.length - 1; // Exclude header row
                    if (recordCount > 500000) {
                        reject(new Error(`CSV file contains ${recordCount.toLocaleString()} records. Maximum allowed is 500,000 records per upload.`));
                        return;
                    }

                    // Parse header row to find "Phone number" column
                    const headerLine = lines[0].trim();
                    const headers = headerLine.split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));

                    // Find the "Phone number" column index
                    const phoneColumnIndex = headers.findIndex(header =>
                        header.includes('phone number') ||
                        header.includes('phone_number') ||
                        header.includes('phonenumber') ||
                        header === 'phone'
                    );

                    if (phoneColumnIndex === -1) {
                        reject(new Error('CSV file must have a "Phone number" column. Found columns: ' + headers.join(', ')));
                        return;
                    }

                    // Extract phone numbers from the correct column
                    const phoneData = lines.slice(1).map(line => {
                        const columns = line.split(',');
                        return columns[phoneColumnIndex] ? columns[phoneColumnIndex].trim().replace(/"/g, '') : '';
                    }).filter(phone => phone);

                    // Validate phone numbers
                    const validPhones = [];
                    const invalidPhones = [];

                    phoneData.forEach((phone, index) => {
                        // Basic phone validation - digits with optional + and spaces
                        const cleanPhone = phone.replace(/\s/g, '');
                        if (/^[\+]?[\d\-\(\)]{10,15}$/.test(cleanPhone)) {
                            validPhones.push(cleanPhone);
                        } else {
                            invalidPhones.push({ line: index + 2, phone }); // +2 because we start from line 2 (after header)
                        }
                    });

                    if (validPhones.length === 0) {
                        reject(new Error('No valid phone numbers found in the "Phone number" column'));
                        return;
                    }

                    // Remove duplicates
                    const uniquePhones = [...new Set(validPhones)];

                    // Check if unique phone numbers exceed limit
                    if (uniquePhones.length > 500000) {
                        reject(new Error(`After removing duplicates, file contains ${uniquePhones.length.toLocaleString()} unique phone numbers. Maximum allowed is 500,000 records per upload.`));
                        return;
                    }

                    resolve({
                        totalNumbers: uniquePhones.length,
                        validPhones: uniquePhones,
                        invalidCount: invalidPhones.length,
                        invalidPhones: invalidPhones.slice(0, 5), // Show first 5 invalid numbers
                        phoneColumnIndex,
                        headerFound: headers[phoneColumnIndex]
                    });
                } catch (error) {
                    reject(new Error('Failed to parse CSV file: ' + error.message));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsText(file);
        });
    };

    const handleFileChange = async (e, fileType) => {
        const file = e.target.files[0];
        if (!file) return;

        const fileKey = fileType === 'csv' ? 'csvFile' : fileType === 'audio' ? 'audioFile' : 'mediaFile';

        // File type validation
        if (fileType === 'csv') {
            const allowedTypes = ['.csv'];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

            if (!allowedTypes.includes(fileExtension)) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: 'Please upload only CSV files'
                }));
                return;
            }

            // Validate CSV content
            try {
                const validation = await validateCSVFile(file);
                setPhoneNumbers(validation.validPhones);
                setFileInfo(prev => ({
                    ...prev,
                    csv: {
                        name: file.name,
                        size: (file.size / 1024).toFixed(2) + ' KB',
                        totalNumbers: validation.totalNumbers,
                        invalidCount: validation.invalidCount,
                        invalidPhones: validation.invalidPhones,
                        status: 'valid'
                    }
                }));
            } catch (error) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: error.message
                }));
                return;
            }
        } else if (fileType === 'audio') {
            const allowedTypes = ['.mp3', '.wav', '.m4a', '.aac'];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

            if (!allowedTypes.includes(fileExtension)) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: 'Please upload only audio files (MP3, WAV, M4A, AAC)'
                }));
                return;
            }

            setFileInfo(prev => ({
                ...prev,
                audio: {
                    name: file.name,
                    size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
                    type: fileExtension.toUpperCase().substring(1),
                    status: 'valid'
                }
            }));
        } else if (fileType === 'media') {
            const imageTypes = ['.jpg', '.jpeg', '.png', '.gif'];
            const documentTypes = ['.pdf'];
            const videoTypes = ['.mp4', '.mov', '.avi'];
            const audioTypes = ['.mp3', '.wav', '.m4a', '.aac'];
            const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

            if (!imageTypes.includes(fileExtension) && !documentTypes.includes(fileExtension) && !videoTypes.includes(fileExtension) && !audioTypes.includes(fileExtension)) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: 'Please upload image, PDF, video, or audio files'
                }));
                return;
            }

            // Different size limits for supported WhatsApp media types
            const isImage = imageTypes.includes(fileExtension);
            const isDocument = documentTypes.includes(fileExtension);
            const isVideo = videoTypes.includes(fileExtension);
            const isAudio = audioTypes.includes(fileExtension);
            const maxSizeImage = process.env.REACT_APP_MEDIA_MAX_SIZE_IMAGE || 1 * 1024 * 1024; // 1MB for images
            const maxSizeVideo = process.env.REACT_APP_MEDIA_MAX_SIZE_VIDEO || 16 * 1024 * 1024; // 16MB for videos
            const maxSizeDocument = process.env.REACT_APP_MEDIA_MAX_SIZE_DOCUMENT || 10 * 1024 * 1024; // 10MB for PDFs
            const maxSizeAudio = process.env.REACT_APP_MEDIA_MAX_SIZE_AUDIO || 50 * 1024 * 1024; // 50MB for audio

            if (isImage && file.size > maxSizeImage) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: `Image file size must be less than ${maxSizeImage / (1024 * 1024)}MB`
                }));
                return;
            }

            if (isVideo && file.size > maxSizeVideo) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: `Video file size must be less than ${maxSizeVideo / (1024 * 1024)}MB`
                }));
                return;
            }

            if (isDocument && file.size > maxSizeDocument) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: `PDF file size must be less than ${maxSizeDocument / (1024 * 1024)}MB`
                }));
                return;
            }

            if (isAudio && file.size > maxSizeAudio) {
                setErrors(prev => ({
                    ...prev,
                    [fileKey]: `Audio file size must be less than ${maxSizeAudio / (1024 * 1024)}MB`
                }));
                return;
            }

            setFileInfo(prev => ({
                ...prev,
                media: {
                    name: file.name,
                    size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
                    type: fileExtension.toUpperCase().substring(1),
                    mediaType: isImage ? 'Image' : isDocument ? 'PDF' : isVideo ? 'Video' : 'Audio',
                    status: 'valid'
                }
            }));
        }

        // File size validation for audio files (50MB limit)
        if (fileType === 'audio' && file.size > 50 * 1024 * 1024) {
            setErrors(prev => ({
                ...prev,
                [fileKey]: 'Audio file size must be less than 50MB'
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [fileKey]: file
        }));

        // Clear error
        setErrors(prev => ({
            ...prev,
            [fileKey]: '',
            ...(fileType === 'media' && formData.campaignType === 'Whatsapp' ? { message: '' } : {})
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.campaignName.trim()) {
            newErrors.campaignName = 'Campaign name is required';
        }

        // Phone number validation based on selected option
        if (formData.phoneNumberOption === 'csv') {
            if (!formData.csvFile) {
                newErrors.csvFile = 'CSV file with phone numbers is required';
            }
        } else {
            if (!formData.manualPhoneNumbers.trim()) {
                newErrors.manualPhoneNumbers = 'Phone numbers are required';
            } else {
                const validation = validateManualPhoneNumbers(formData.manualPhoneNumbers);
                if (validation.validPhones.length === 0) {
                    newErrors.manualPhoneNumbers = 'Please enter at least one valid phone number';
                }
            }
        }

        if (formData.campaignType === 'SMS') {
            if (!formData.message.trim()) {
                newErrors.message = 'Message is required for SMS campaigns';
            }
        }

        if (formData.campaignType === 'Voice Call' && !formData.audioFile) {
            newErrors.audioFile = 'Audio file is required for voice call campaigns';
        }

        if (formData.campaignType === 'Whatsapp' && !formData.message.trim() && !formData.mediaFile) {
            newErrors.mediaFile = 'For WhatsApp, add either a message or a media file';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Real-time progress connection via Server-Sent Events
    const connectToProgressStream = (campaignId) => {
        const token = localStorage.getItem('token');
        const url = `/api/campaigns/progress/${campaignId}?token=${encodeURIComponent(token)}`;

        const es = new EventSource(url);

        es.onopen = () => {};

        es.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === 'connected') {
                    // SSE connected
                } else if (data.type === 'progress') {
                    // Update progress with real backend data
                    setUploadProgress(data.progress || 0);
                    setUploadStage(data.stage || 'processing');
                    setProcessedRecords(data.processedRecords || 0);
                    setValidRecords(data.validRecords || 0);
                    setRecordsWritten(data.writtenRecords || 0);
                    setRecordsPending(data.pendingRecords || 0);

                    // Calculate write progress percentage
                    if (data.processedRecords > 0) {
                        const writePercentage = Math.round((data.writtenRecords / data.processedRecords) * 100);
                        setWriteProgress(writePercentage);
                    }
                } else if (data.type === 'error') {
                    setUploadError(data.message || 'Authentication error');
                    es.close();
                }
            } catch (error) {
                // SSE parse error - ignore
            }
        };

        es.onerror = () => {
            es.close();
        };

        setEventSource(es);
        return es;
    };

    // Cleanup SSE connection
    const disconnectProgressStream = () => {
        if (eventSource) {
            eventSource.close();
            setEventSource(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the form errors');
            return;
        }

        setLoading(true);
        setUploadProgress(0);
        setUploadStage('uploading');
        setUploadError(null);

        try {
            const submitData = new FormData();
            submitData.append('campaignName', formData.campaignName);
            submitData.append('campaignType', formData.campaignType);
            submitData.append('phoneNumberOption', formData.phoneNumberOption);

            if (formData.phoneNumberOption === 'csv') {
                submitData.append('csvFile', formData.csvFile);
            } else {
                submitData.append('manualPhoneNumbers', formData.manualPhoneNumbers);
            }

            if (formData.message) {
                submitData.append('message', formData.message);
            }

            if (formData.audioFile) {
                submitData.append('audioFile', formData.audioFile);
            }

            if (formData.mediaFile) {
                submitData.append('mediaFile', formData.mediaFile);
            }

            // Set initial estimates
            if (formData.phoneNumberOption === 'csv' && fileInfo.csv) {
                setTotalRecords(fileInfo.csv.totalNumbers || 0);
            } else {
                setTotalRecords(phoneNumbers.length);
            }

            let lastProgressTime = 0;
            let lastProgressLoaded = 0;

            // Start backend processing simulation after upload completes
            const simulateProcessingProgress = () => {
                const totalRecords = formData.phoneNumberOption === 'csv' && fileInfo.csv ?
                    fileInfo.csv.totalNumbers : phoneNumbers.length;

                setUploadStage('parsing');
                setTotalRecords(totalRecords);
                setRecordsPending(totalRecords);

                let processed = 0;
                let isCompleted = false;
                const increment = Math.max(1, Math.floor(totalRecords / 300)); // Slower progress, ~300 updates

                const progressInterval = setInterval(() => {
                    if (isCompleted) {
                        clearInterval(progressInterval);
                        return;
                    }

                    // Don't complete simulation until we're at 90% to leave room for backend
                    const maxProgress = Math.floor(totalRecords * 0.9);
                    processed = Math.min(processed + increment, maxProgress);
                    setProcessedRecords(processed);

                    const progressPercent = (processed / totalRecords) * 100;

                    // Progress through stages based on completion percentage
                    if (progressPercent < 20) {
                        setUploadStage('parsing');
                    } else if (progressPercent < 40) {
                        setUploadStage('validating');
                    } else if (progressPercent < 65) {
                        setUploadStage('processing');
                    } else {
                        setUploadStage('writing');
                        const written = Math.floor((progressPercent - 65) / 25 * totalRecords);
                        setRecordsWritten(written);
                        setRecordsPending(Math.max(0, totalRecords - written));
                        setWriteProgress(Math.min(90, ((progressPercent - 65) / 25) * 100));
                    }
                }, 80); // Slower updates every 80ms

                // Return both interval and completion function
                return {
                    interval: progressInterval,
                    complete: () => {
                        isCompleted = true;
                        clearInterval(progressInterval);
                        // Final completion state
                        setProcessedRecords(totalRecords);
                        setRecordsWritten(totalRecords);
                        setRecordsPending(0);
                        setWriteProgress(100);
                        setUploadStage('completed');
                    }
                };
            };

            let processingSimulation;
            const response = await campaignAPI.createUserCampaign(submitData, {
                timeout: 2400000, // 40 minutes timeout for very large CSV uploads (1M+ records)
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.lengthComputable) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percentCompleted);

                        // Calculate upload speed
                        const currentTime = Date.now();
                        if (lastProgressTime) {
                            const timeDiff = (currentTime - lastProgressTime) / 1000; // in seconds
                            const bytesDiff = progressEvent.loaded - lastProgressLoaded;
                            const speed = bytesDiff / timeDiff; // bytes per second
                            setUploadSpeed(speed);
                        }
                        lastProgressTime = currentTime;
                        lastProgressLoaded = progressEvent.loaded;

                        // Start processing simulation when upload reaches 100%
                        if (percentCompleted >= 100 && !processingSimulation) {
                            processingSimulation = simulateProcessingProgress();
                        }
                    }
                }
            });

            // Set initial estimates for progress tracking
            if (formData.phoneNumberOption === 'csv' && fileInfo.csv) {
                setTotalRecords(fileInfo.csv.totalNumbers || 0);
                setRecordsPending(fileInfo.csv.totalNumbers || 0);
            } else {
                setTotalRecords(phoneNumbers.length);
                setRecordsPending(phoneNumbers.length);
            }

            if (response.data.success) {
                const responseData = response.data.data;
                const { creditsDeducted, totalContacts, campaignId } = responseData;

                // Connect to real-time progress stream immediately
                if (campaignId) {
                    connectToProgressStream(campaignId);
                }

                // Complete the simulation first (this will set the stage to 'completed')
                if (processingSimulation) {
                    processingSimulation.complete();
                }

                // Update final progress states with real data from backend
                setUploadProgress(100);
                setProcessedRecords(totalContacts || 0);
                setValidRecords(totalContacts || 0);
                setRecordsWritten(totalContacts || 0);
                setRecordsPending(0);
                setWriteProgress(100);

                // Extract processing statistics if available
                if (responseData.invalidCount) setInvalidRecords(responseData.invalidCount);
                if (responseData.processingDetails?.validationSummary) {
                    const summary = responseData.processingDetails.validationSummary;
                    setInvalidRecords(summary.phoneErrors + summary.statusErrors + summary.timestampErrors + summary.pulseErrors);
                    setDuplicateRecords(summary.duplicateErrors);
                }

                setTimeout(() => {
                    // Fetch updated user profile from server to ensure data consistency
                    try {
                        authAPI.getProfile().then(profileResponse => {
                            if (profileResponse.data.success) {
                                updateUser(profileResponse.data.data.user);
                            }
                        });
                    } catch (profileError) {
                        // Silent fallback - update credits locally
                        // Fallback: Update credits locally
                        const updatedUser = { ...user };
                        if (formData.campaignType === 'Voice Call') {
                            updatedUser.voiceCallCredits -= creditsDeducted;
                        } else if (formData.campaignType === 'SMS') {
                            updatedUser.smsCredits -= creditsDeducted;
                        } else if (formData.campaignType === 'Whatsapp') {
                            updatedUser.whatsappCredits -= creditsDeducted;
                        }
                        updateUser(updatedUser);
                    }

                    toast.success(`Campaign created successfully! ${creditsDeducted} credits deducted.`);

                    // Disconnect SSE before navigation
                    disconnectProgressStream();
                    navigate('/campaigns');
                }, 2000);
            } else {
                setUploadStage('error');
                setUploadError(response.data.message || 'Failed to create campaign');
                toast.error(response.data.message || 'Failed to create campaign');
            }
        } catch (error) {
            let errorMessage = error.response?.data?.message || 'Failed to create campaign';

            // Provide specific message for timeout errors
            if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
                errorMessage = 'Upload is taking longer than expected (>20 minutes). This might happen with very large CSV files (1M+ records). Please try with a smaller file or contact support if the issue persists.';
            }

            setUploadStage('error');
            setUploadError(errorMessage);
            toast.error(errorMessage);
        } finally {
            // Don't reset loading immediately for better UX with progress modal
            setTimeout(() => {
                setLoading(false);
                setUploadProgress(0);
                setUploadStage('uploading');
                setUploadSpeed(0);
                setProcessedRecords(0);
                setValidRecords(0);
                setInvalidRecords(0);
                setDuplicateRecords(0);
                setRecordsWritten(0);
                setRecordsPending(0);
                setWriteProgress(0);
                setUploadError(null);
            }, 3000);
        }
    };

    const renderCampaignTypeSelector = () => {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {campaignTypeOptions.map((option) => {
                    const IconComponent = option.icon;
                    const isSelected = formData.campaignType === option.value;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => handleCampaignTypeChange(option.value)}
                            className={`p-3 sm:p-4 border-2 rounded-lg text-left transition-all duration-200 ${isSelected
                                ? 'border-primary-500 bg-primary-50 text-primary-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                }`}
                        >
                            <div className="flex items-center space-x-2 sm:space-x-3">
                                <IconComponent className={`h-5 w-5 sm:h-6 sm:w-6 ${isSelected ? 'text-primary-600' : 'text-gray-500'}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="font-medium text-sm sm:text-base truncate">{option.label}</div>
                                    <div className="text-xs sm:text-sm text-gray-500 truncate">{option.description}</div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-soft-900">Create New Campaign</h1>
                    <p className="text-soft-600 mt-1">Create and launch your communication campaign</p>
                </div>
            </div>

            {/* Form */}
            <div className="card">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Campaign Name */}
                    <div>
                        <label className="block text-sm font-medium text-soft-700 mb-2">
                            Campaign Name *
                        </label>
                        <input
                            type="text"
                            name="campaignName"
                            value={formData.campaignName}
                            onChange={handleInputChange}
                            className={`input-field ${errors.campaignName ? 'border-red-500' : ''}`}
                            placeholder="Enter campaign name..."
                            required
                        />
                        {errors.campaignName && <p className="text-red-500 text-sm mt-1">{errors.campaignName}</p>}
                    </div>

                    {/* Campaign Type */}
                    <div>
                        <label className="block text-sm font-medium text-soft-700 mb-2">
                            Campaign Type *
                        </label>
                        {renderCampaignTypeSelector()}
                    </div>

                    {/* Dynamic Fields Based on Campaign Type */}
                    {(formData.campaignType === 'SMS' || formData.campaignType === 'Whatsapp') && (
                        <div>
                            <label className="block text-sm font-medium text-soft-700 mb-2">
                                Message {formData.campaignType === 'SMS' ? '*' : '(Optional)'}
                            </label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                rows="4"
                                className={`input-field ${errors.message ? 'border-red-500' : ''}`}
                                placeholder={formData.campaignType === 'Whatsapp' ? 'Enter message or upload media...' : 'Enter your message...'}
                                required={formData.campaignType === 'SMS'}
                            />
                            {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                            {formData.campaignType === 'Whatsapp' && (
                                <p className="text-xs text-soft-500 mt-1">
                                    For WhatsApp, you can submit a message, a media file, or both.
                                </p>
                            )}
                            <p className="text-xs text-soft-500 mt-1">
                                Characters: {formData.message.length}
                            </p>
                        </div>
                    )}

                    {/* Audio File Upload for Voice Calls */}
                    {formData.campaignType === 'Voice Call' && (
                        <div>
                            <label className="block text-sm font-medium text-soft-700 mb-2">
                                <MusicalNoteIcon className="inline h-4 w-4 mr-1" />
                                Audio File *
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                                <input
                                    type="file"
                                    onChange={(e) => handleFileChange(e, 'audio')}
                                    accept=".mp3,.wav,.m4a,.aac"
                                    className="hidden"
                                    id="audioFile"
                                />
                                <label
                                    htmlFor="audioFile"
                                    className="cursor-pointer flex flex-col items-center"
                                >
                                    <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mb-2" />
                                    <span className="text-sm font-medium text-gray-700">Click to upload audio file</span>
                                    <span className="text-xs text-gray-500">MP3, WAV, M4A, AAC up to 50MB</span>
                                </label>
                            </div>
                            {fileInfo.audio && (
                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center space-x-2">
                                        <CheckIcon className="h-5 w-5 text-green-600" />
                                        <div>
                                            <p className="text-sm font-medium text-green-800">{fileInfo.audio.name}</p>
                                            <p className="text-xs text-green-600">{fileInfo.audio.size} • {fileInfo.audio.type}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {errors.audioFile && <p className="text-red-500 text-sm mt-1">{errors.audioFile}</p>}
                        </div>
                    )}

                    {/* Media File Upload for WhatsApp */}
                    {formData.campaignType === 'Whatsapp' && (
                        <div>
                            <label className="block text-sm font-medium text-soft-700 mb-2">
                                <DocumentArrowUpIcon className="inline h-4 w-4 mr-1" />
                                Media File {formData.message.trim() ? '(Optional)' : '(Optional if message added)'}
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                                <input
                                    type="file"
                                    onChange={(e) => handleFileChange(e, 'media')}
                                    accept=".jpg,.jpeg,.png,.gif,.pdf,.mp4,.mov,.avi,.mp3,.wav,.m4a,.aac"
                                    className="hidden"
                                    id="mediaFile"
                                />
                                <label
                                    htmlFor="mediaFile"
                                    className="cursor-pointer flex flex-col items-center"
                                >
                                    <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mb-2" />
                                    <span className="text-sm font-medium text-gray-700">Click to upload media file</span>
                                    <span className="text-xs text-gray-500">Images, PDF, video, and audio are supported</span>
                                </label>
                            </div>
                            {fileInfo.media && (
                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center space-x-2">
                                        <CheckIcon className="h-5 w-5 text-green-600" />
                                        <div>
                                            <p className="text-sm font-medium text-green-800">{fileInfo.media.name}</p>
                                            <p className="text-xs text-green-600">
                                                {fileInfo.media.size} • {fileInfo.media.type} • {fileInfo.media.mediaType}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {errors.mediaFile && <p className="text-red-500 text-sm mt-1">{errors.mediaFile}</p>}
                        </div>
                    )}

                    {/* Phone Numbers Input Method Selection */}
                    <div>
                        <label className="block text-sm font-medium text-soft-700 mb-2">
                            <DocumentArrowUpIcon className="inline h-4 w-4 mr-1" />
                            Phone Numbers *
                        </label>

                        {/* Option Selection */}
                        <div className="mb-4">
                            <div className="flex space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="phoneNumberOption"
                                        value="manual"
                                        checked={formData.phoneNumberOption === 'manual'}
                                        onChange={(e) => handlePhoneNumberOptionChange(e.target.value)}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Enter manually</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="phoneNumberOption"
                                        value="csv"
                                        checked={formData.phoneNumberOption === 'csv'}
                                        onChange={(e) => handlePhoneNumberOptionChange(e.target.value)}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Upload CSV file</span>
                                </label>
                            </div>
                        </div>

                        {/* Manual Phone Number Entry */}
                        {formData.phoneNumberOption === 'manual' && (
                            <div>
                                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-medium text-blue-800 mb-2">Manual Entry:</h4>
                                    <div className="text-xs text-blue-700 space-y-1">
                                        <div>• Enter phone numbers separated by commas</div>
                                        <div>• Supported formats: +1234567890, 1234567890, (123) 456-7890</div>
                                        <div>• Duplicates will be automatically removed</div>
                                    </div>
                                </div>

                                <textarea
                                    name="manualPhoneNumbers"
                                    value={formData.manualPhoneNumbers}
                                    onChange={(e) => {
                                        handleInputChange(e);
                                        // Real-time validation feedback
                                        const validation = validateManualPhoneNumbers(e.target.value);
                                        setPhoneNumbers(validation.validPhones);
                                    }}
                                    rows="6"
                                    className={`input-field ${errors.manualPhoneNumbers ? 'border-red-500' : ''}`}
                                    placeholder="Enter phone numbers separated by commas, e.g., +1234567890, 9876543210, (555) 123-4567"
                                />

                                {formData.manualPhoneNumbers && (
                                    <div className="mt-2 text-sm">
                                        {(() => {
                                            const validation = validateManualPhoneNumbers(formData.manualPhoneNumbers);
                                            return (
                                                <div className="p-2 bg-gray-50 rounded border">
                                                    <p className="text-green-600">
                                                        <strong>Valid numbers:</strong> {validation.validPhones.length}
                                                    </p>
                                                    {validation.invalidPhones.length > 0 && (
                                                        <p className="text-red-600">
                                                            <strong>Invalid numbers:</strong> {validation.invalidPhones.length}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                {errors.manualPhoneNumbers && (
                                    <p className="text-red-500 text-sm mt-1">{errors.manualPhoneNumbers}</p>
                                )}
                            </div>
                        )}

                        {/* CSV File Upload */}
                        {formData.phoneNumberOption === 'csv' && (
                            <div>
                                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-medium text-blue-800 mb-2">CSV Format Required:</h4>
                                    <div className="text-xs text-blue-700 space-y-1">
                                        <div>• Must have a header row with "Phone number" column</div>
                                        <div>• Accepted column names: "Phone number", "Phone_number", "PhoneNumber", "Phone"</div>
                                        <div>• Supported formats: +1234567890, 1234567890, (123) 456-7890</div>
                                        <div>• Duplicates will be automatically removed</div>
                                    </div>
                                </div>

                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                                    <input
                                        type="file"
                                        onChange={(e) => handleFileChange(e, 'csv')}
                                        accept=".csv"
                                        className="hidden"
                                        id="csvFile"
                                    />
                                    <label
                                        htmlFor="csvFile"
                                        className="cursor-pointer flex flex-col items-center"
                                    >
                                        <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mb-2" />
                                        <span className="text-sm font-medium text-gray-700">Click to upload CSV file</span>
                                        <span className="text-xs text-gray-500">CSV files with "Phone number" column (Max 500,000 records)</span>
                                    </label>
                                </div>

                                {fileInfo.csv && (
                                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <CheckIcon className="h-5 w-5 text-green-600" />
                                            <div>
                                                <p className="text-sm font-medium text-green-800">{fileInfo.csv.name}</p>
                                                <p className="text-xs text-green-600">{fileInfo.csv.size}</p>
                                            </div>
                                        </div>
                                        <div className="text-sm text-green-700">
                                            <p><strong>Column found:</strong> "{fileInfo.csv.headerFound}"</p>
                                            <p><strong>Valid numbers:</strong> {fileInfo.csv.totalNumbers}</p>
                                            {fileInfo.csv.invalidCount > 0 && (
                                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                                    <p className="text-sm text-yellow-800">
                                                        <ExclamationTriangleIcon className="inline h-4 w-4 mr-1" />
                                                        {fileInfo.csv.invalidCount} invalid numbers will be skipped
                                                    </p>
                                                    {fileInfo.csv.invalidPhones.length > 0 && (
                                                        <div className="mt-1 text-xs text-yellow-700">
                                                            Examples: {fileInfo.csv.invalidPhones.map(p => p.phone).join(', ')}
                                                            {fileInfo.csv.invalidCount > 5 && ` and ${fileInfo.csv.invalidCount - 5} more...`}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {errors.csvFile && <p className="text-red-500 text-sm mt-1">{errors.csvFile}</p>}
                            </div>
                        )}
                    </div>

                    {/* Enhanced Upload Progress Modal - Only show for CSV uploads */}
                    {loading && formData.phoneNumberOption === 'csv' && (
                        <FileUploadProgress
                            progress={uploadProgress}
                            stage={uploadStage}
                            fileName={formData.csvFile?.name || ''}
                            fileSize={formData.csvFile?.size || 0}
                            uploadSpeed={uploadSpeed}
                            processedRecords={processedRecords}
                            totalRecords={totalRecords}
                            validRecords={validRecords}
                            invalidRecords={invalidRecords}
                            duplicateRecords={duplicateRecords}
                            recordsWritten={recordsWritten}
                            recordsPending={recordsPending}
                            writeProgress={writeProgress}
                            error={uploadError}
                            onCancel={() => {
                                setLoading(false);
                                setUploadProgress(0);
                                setUploadStage('uploading');
                                setUploadSpeed(0);
                                setProcessedRecords(0);
                                setValidRecords(0);
                                setInvalidRecords(0);
                                setDuplicateRecords(0);
                                setRecordsWritten(0);
                                setRecordsPending(0);
                                setWriteProgress(0);
                                setUploadError(null);
                            }}
                        />
                    )}

                    {/* Simple Loading Modal for Manual Entry */}
                    {loading && formData.phoneNumberOption === 'manual' && (
                        <div className="upload-modal fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                            <div className="modal-content bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-fade-in">
                                <div className="p-6">
                                    <div className="flex flex-col items-center text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Creating Campaign</h3>
                                        <p className="text-sm text-gray-600">
                                            Processing {phoneNumbers.length} phone numbers...
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Summary */}
                    {((formData.phoneNumberOption === 'csv' && fileInfo.csv) ||
                        (formData.phoneNumberOption === 'manual' && formData.manualPhoneNumbers)) && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-medium text-gray-800 mb-2">Campaign Summary</h3>
                                <div className="text-sm text-gray-600 space-y-1">
                                    <p><strong>Campaign:</strong> {formData.campaignName || 'Unnamed Campaign'}</p>
                                    <p><strong>Type:</strong> {formData.campaignType}</p>
                                    <p><strong>Phone Input:</strong> {formData.phoneNumberOption === 'csv' ? 'CSV Upload' : 'Manual Entry'}</p>
                                    <p><strong>Target Numbers:</strong> {
                                        formData.phoneNumberOption === 'csv'
                                            ? fileInfo.csv?.totalNumbers || 0
                                            : validateManualPhoneNumbers(formData.manualPhoneNumbers).validPhones.length
                                    }</p>
                                    {formData.phoneNumberOption === 'csv' && fileInfo.csv?.headerFound && (
                                        <p><strong>CSV Column:</strong> "{fileInfo.csv.headerFound}"</p>
                                    )}
                                </div>
                            </div>
                        )}

                    {/* Submit Buttons */}
                    <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-6 border-t border-soft-200">
                        <button
                            type="button"
                            onClick={() => navigate('/campaigns')}
                            className="btn-secondary w-full sm:w-auto"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto ${loading ? 'cursor-not-allowed' : ''}`}
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    <span className="hidden sm:inline">Creating...</span>
                                    <span className="sm:hidden">Creating</span>
                                </>
                            ) : (
                                <>
                                    <PlusIcon className="h-4 w-4" />
                                    <span className="hidden sm:inline">Create Campaign</span>
                                    <span className="sm:hidden">Create</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserCreateCampaign; 